import React, { ChangeEvent, FC, MutableRefObject, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import close from 'src/front/assets/search-close.svg';
import search from 'src/front/assets/search.svg';
import { formatAlphaNumericCyrillicPlusBlank } from 'src/front/common/utils/formUtils';
import { BREAK_POINTS } from 'src/front/constant';
import styled from 'styled-components';

interface Props {
  onChange: (event: any) => void;
  minimumSearchCharacters: number;
  noResult: boolean;
}

const SearchIcon = styled.img`
  position: absolute;
  top: 24.5px;
  left: 9px;
`;

const CloseIcon = styled.img`
  position: absolute;
  top: 24.5px;
  right: 15px;
  cursor: pointer;
`;

const Container = styled.div`
  position: relative;
  z-index: 3;

  height: auto;
  border: 1px solid gray;
  border-radius: 3px;
  margin: 1rem 0.25em;
  margin-left: 5px;
  margin-right: 35px;
  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) and (min-width: ${BREAK_POINTS.MIN
      .PHONE}) {
    margin-right: 23px;
  }
  @media screen and (max-width: ${BREAK_POINTS.MAX.PHONE}) {
    margin-right: 3px;
  }
`;

const SearchInput = styled.input`
  width: calc(100% - 5em);
  height: 60px;
  outline: none;

  padding-left: 29px;
  border: none;
  border-radius: 3px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.whiteThree};

  color: ${({ theme }) => theme.palette.black};
  ${({ theme }) => `font-family: ${theme.fonts.GothamMedium};`}
  font-size: 13px;
  &::placeholder {
    color: ${({ theme }) => theme.palette.blueGrey};
  }
`;

const NoResultText = styled.p`
  padding: 25px 0;
  border-top: none;

  margin: 0;
  margin-top: 50px;

  color: ${({ theme }) => theme.palette.deepSeaBlue};

  font-size: 16px;
  font-weight: 400;
  ${({ theme }) => `font-family: ${theme.fonts.GothamMedium};`}
  text-align: center;
`;

const SearchBar: FC<Props> = ({ onChange, minimumSearchCharacters, noResult }) => {
  const [searchText, setSearchText] = useState('');
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>();

  const handleClearSearch = (): void => {
    setSearchText('');
    onChange('');
  };

  const handleChangeInput = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    const trimmedValue = formatAlphaNumericCyrillicPlusBlank(target.value);

    if (searchText === trimmedValue) return;
    setSearchText(trimmedValue);

    if (trimmedValue.length < minimumSearchCharacters) {
      if (trimmedValue === '') handleClearSearch();
      return;
    }
    onChange(trimmedValue.toUpperCase());
  };

  return (
    <>
      <Container>
        <SearchIcon alt="search icon" src={search} />
        <SearchInput
          maxLength={100}
          onChange={handleChangeInput}
          placeholder={t('general_components_menuSearch_label')}
          ref={searchInputRef as MutableRefObject<HTMLInputElement>}
          value={searchText}
        />
        <CloseIcon alt="close icon" onClick={handleClearSearch} src={close} />
      </Container>
      {noResult ? (
        <NoResultText>{t('general_components_menuSearchNoResult_errorMessage')}</NoResultText>
      ) : null}
    </>
  );
};

export default SearchBar;
